import React, { useEffect, useState, useContext } from "react";
import useDataFetch, { usePostDataFetch, useDeleteDataFetch } from "../../utils/useDataFetch";
import {
    Box, Card, CardContent, Typography, CircularProgress, Button, Grid, Divider, Alert, Stack, IconButton,
    Drawer, TextField, MenuItem, Select, FormControl, InputLabel, useTheme, Fade
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ActiveTeamContext } from "../../context/ActiveTeamContext";
import { useAuth } from '../../context/AuthContext';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CreateTermin from "./CreateTermin";
import SingleTerminCard from "./SingleTerminCard";

const AllTermine = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { activeTeamId } = useContext(ActiveTeamContext);
    const { data: profile } = useDataFetch({ url: '/users/profile' });

    // Evaluate Role
    const userGlobalRole = profile?.user?.role;
    const activeTeamRole = profile?.teams?.find(t => t?.team_id?.toString() === activeTeamId?.toString())?.rolle;
    const isTrainer = userGlobalRole === 'trainer' || profile?.user?.isAdmin || userGlobalRole === 'super_admin' || activeTeamRole === 'trainer' || activeTeamRole === 'admin';

    const { data, error, isLoading, refetch } = useDataFetch({
        url: activeTeamId && user?.id ? `/termine/allAppointments?id=${user.id}&teamId=${activeTeamId}` : null
    });

    const { deleteData } = useDeleteDataFetch();
    const navigate = useNavigate();

    const [openModal, setOpenModal] = useState(false);
    const [editTermin, setEditTermin] = useState(null);
    const [filterType, setFilterType] = useState('Alle');
    const [viewMode, setViewMode] = useState('upcoming');
    const [pastFilter, setPastFilter] = useState('30days');

    useEffect(() => {
        if (viewMode === 'past' && pastFilter === '') {
            setPastFilter('30days');
        }
    }, [viewMode, pastFilter]);

    const handleDelete = async (id, e) => {
        if (e?.stopPropagation) {
            e.stopPropagation();
        }
        if (window.confirm("Bist du sicher, dass du diesen Termin unwiderruflich löschen möchtest?")) {
            await deleteData({ url: `/termine/${id}` });
            refetch();
        }
    };

    const handleEdit = (termin, e) => {
        if (e?.stopPropagation) {
            e.stopPropagation();
        }
        setEditTermin(termin);
        setOpenModal(true);
    };

    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    const allTermine = data?.appointments || [];

    // Process Termine
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let processedTermine = filterType === 'Alle' ? allTermine : allTermine.filter(t => t.typ === filterType);

    if (viewMode === 'upcoming') {
        processedTermine = processedTermine.filter(t => {
            if (!t.dat) return true;
            const tDate = new Date(t.dat);
            tDate.setHours(0, 0, 0, 0);
            return tDate >= today;
        });
        processedTermine.sort((a, b) => new Date(a.dat || '2099-01-01') - new Date(b.dat || '2099-01-01'));
    } else {
        processedTermine = processedTermine.filter(t => {
            if (!t.dat) return false;
            const tDate = new Date(t.dat);
            tDate.setHours(0, 0, 0, 0);
            if (tDate >= today) return false;

            if (pastFilter === 'all') return true;
            const limitDate = new Date(today);
            if (pastFilter === '30days') limitDate.setDate(limitDate.getDate() - 30);
            if (pastFilter === '3months') limitDate.setMonth(limitDate.getMonth() - 3);
            return tDate >= limitDate;
        });
        processedTermine.sort((a, b) => new Date(b.dat) - new Date(a.dat));
    }

    if (!localStorage.getItem('activeTeamId')) {
        return (
            <Typography >Kein aktives Team ausgewählt. Bitte wähle ein Team aus, um Termine zu erstellen.</Typography>
        );
    }

    return (
        <Fade in timeout={800}>
            <Box sx={{ mt: 3, pb: 10 }}>
                <Card sx={{ mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.95)' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel><FilterListIcon sx={{ fontSize: 18, mr: 0.5 }} /> Typ</InputLabel>
                                    <Select
                                        value={filterType}
                                        label="Typ"
                                        onChange={(e) => setFilterType(e.target.value)}
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        <MenuItem value="Alle">Alle</MenuItem>
                                        <MenuItem value="Training">Training</MenuItem>
                                        <MenuItem value="Spiel">Spiel</MenuItem>
                                        <MenuItem value="Event">Event</MenuItem>
                                        <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value)}
                                        sx={{ borderRadius: '12px' }}
                                    >
                                        <MenuItem value="upcoming">Zukünftige</MenuItem>
                                        <MenuItem value="past">Vergangene</MenuItem>
                                    </Select>
                                </FormControl>

                                {viewMode === 'past' && (
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <Select
                                            value={pastFilter}
                                            onChange={(e) => setPastFilter(e.target.value)}
                                            sx={{ borderRadius: '12px' }}
                                        >
                                            <MenuItem value="30days">Letzte 30 Tage</MenuItem>
                                            <MenuItem value="3months">Letzte 3 Monate</MenuItem>
                                            <MenuItem value="all">Alle</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>

                            {isTrainer && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => { setEditTermin(null); setOpenModal(true); }}
                                    sx={{ borderRadius: 2, textTransform: 'none', px: 3, py: 1.5 }}
                                >
                                    Neu
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {processedTermine.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>Keine Termine für diesen Filter gefunden.</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {processedTermine.map(t => (
                            <Grid size={{ xs: 12, md: 6 }} key={t.id}>
                                <SingleTerminCard
                                    termin={t}
                                    isTrainer={isTrainer}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    refetch={refetch}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Drawer
                    anchor="bottom"
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '90vh',
                            maxWidth: 'sm',
                            margin: '0 auto',
                            p: 2
                        }
                    }}
                >
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" fontWeight="bold">
                            {editTermin ? 'Termin bearbeiten' : 'Neuen Termin anlegen'}
                        </Typography>
                        <IconButton onClick={() => setOpenModal(false)}>
                            <HighlightOffIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ overflowY: 'auto' }}>
                        <CreateTermin
                            activeTeamId={activeTeamId}
                            initialData={editTermin}
                            onSucess={() => { setOpenModal(false); refetch(); }}
                        />
                    </Box>
                </Drawer>
            </Box>
        </Fade>
    );
}

export default AllTermine;
